import { useCallback, useRef, useState } from 'react'
import { deleteProvider, PostFormat, postProvider } from 'sharedKernel/api'
import { IMeetingRoomFormData } from '../types'
import { MEETING_ROOM_API } from '../api/constants'
import { buildMeetingRoomSavePayload, extractMeetingRoomSaveId } from '../api/mappers'
import { getMeetingRoomRemovedFileIds } from '../api/meetingRoomFiles'
import { trans } from 'sharedKernel/i18n'

export const MEETING_ROOM_SAVE_IN_PROGRESS = 'MEETING_ROOM_SAVE_IN_PROGRESS'

export const useSaveMeetingRoom = () => {
  const mutation = postProvider()
  const deleteMutation = deleteProvider()
  const [isSaving, setIsSaving] = useState(false)
  const saveInFlightRef = useRef(false)

  const save = useCallback(
    async (form: IMeetingRoomFormData, meetingRoomId?: string): Promise<string> => {
      if (saveInFlightRef.current) {
        throw new Error(MEETING_ROOM_SAVE_IN_PROGRESS)
      }

      saveInFlightRef.current = true
      setIsSaving(true)

      try {
        const payload = await buildMeetingRoomSavePayload(form, meetingRoomId)
        const request: PostFormat = {
          url: MEETING_ROOM_API.BASE,
          data: payload,
          httpConfig: { timeout: 120000 },
        }

        const response = await mutation.mutateAsync(request)
        const savedId = extractMeetingRoomSaveId(response, meetingRoomId)

        if (savedId == null) {
          throw new Error(trans('meetingRoom.error.idNotReturned'))
        }

        const roomIdForDelete = meetingRoomId ?? savedId
        const filesToDelete = getMeetingRoomRemovedFileIds(form)

        if (filesToDelete.length > 0) {
          await Promise.all(
            filesToDelete.map(fileId =>
              deleteMutation.mutateAsync({
                url: MEETING_ROOM_API.fileRemove(roomIdForDelete, fileId),
                httpConfig: { timeout: 120000 },
              })
            )
          )
        }

        return String(savedId)
      } finally {
        saveInFlightRef.current = false
        setIsSaving(false)
      }
    },
    [mutation, deleteMutation]
  )

  return {
    save,
    isLoading: isSaving,
  }
}
